import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Lottie from "lottie-react";
import { useRetrieveStory } from "../../hooks/useRetrieveStory";
import { webRoutes } from "../../router/webRoutes";
import {
  TabType,
  TabMappedToRoom,
  TabToRoomBlockName,
  Block,
  StoryData,
  UserAction,
  childOnboardingTemplateType,
} from "./types";
import RoomTabBar from "./components/RoomTabBar/RoomTabBar";
import YardRoom from "./components/YardRoom/YardRoom";
import HomeRoom from "./components/HomeRoom/HomeRoom";
import PlayRoom from "./components/PlayRoom/PlayRoom";
import CalmRoom from "./components/CalmRoom/CalmRoom";
import OnBoardingWelcomeRoom from "./components/OnBoardingWelcomeRoom/OnBoardingWelcomeRoom";
import OnBoardingWelcomeTemplate from "./components/OnBoardingWelcomeTemplate";
import HeartCorner from "../HeartCorner/HeartCorner";
import { HeartBlock } from "../HeartCorner/types";
import {
  usePostStoryData,
  StoryUpdatePayload,
} from "../../hooks/useHeartCorner";
import publishStarJson from "../../appAssets/jsons/publishStar.json";
import logoutIcon from "../../appAssets/jsons/logoutIcon.json";
import appSwitchIcon from "../../appAssets/jsons/appSwitchIcon.json";
import heartImageJson from "../../appAssets/jsons/heartImage.json";
import "./Rooms.scss";
import WebJourneySplash from "./components/WebJourneySplash";
import { WebLoader } from "../../components/Loader";
import { BackgroundMusicButton } from "../../components/BackgroundMusic";
import WebLottiePlayer from "../WebStoryScreen/Components/WebLottiePlayer";
import clickAnimationJson from "../../appAssets/jsons/ClickAnimation.json";
import apiPaths from "../../../api/endpoints";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import { triggerLogout } from "../../../auth/logout";
import { trackEvent } from "../../../mixpanel";
import { TrackingEvents } from "../../Utilities/analytics";

const EXIT_OPTION_NAMES = [
  "ExitOptionCompleted",
  "ExitOptionInProgress",
  "ExitOptionNotStarted",
  // "ExitOptionFinished", As this includes planner conversation so commented.
];

/**
 * Mirrors mobile resolveActiveBlock / convoDataList logic.
 *
 * Priority:
 *  1. ExitOption block → use the journey block directly (planner UI)
 *  2. Onboarding done  → room-specific intro conversation
 *  3. Default          → first journey / story block
 */
function resolveActiveBlock(
  storyData: StoryData | null,
  activeTab: TabType,
): Block | null {
  if (!storyData) return null;

  const firstJourneyBlock =
    storyData.finalTransformedJourneyPage?.blocks?.[0] ?? null;

  // ExitOption — both onboarding done and not-done paths use the journey block
  // if (
  //   firstJourneyBlock?.name &&
  //   EXIT_OPTION_NAMES.includes(firstJourneyBlock.name)
  // ) {
  //   return firstJourneyBlock;
  // }

  // Onboarding completed — room-specific intro conversations
  if (storyData.isOnboardingCompleted) {
    const introBlocks = storyData.roomIntroConversations?.blocks ?? [];
    const roomName = TabToRoomBlockName[activeTab];
    const roomBlock = introBlocks.find(
      (b) => b.name?.toLowerCase() === roomName.toLowerCase(),
    );
    return roomBlock ?? introBlocks[0] ?? null;
  }

  // Default — first journey or story block
  return firstJourneyBlock ?? storyData.blocks?.[0] ?? null;
}

function Rooms() {
  const { retrieveStoryData } = useRetrieveStory();
  const { postStoryData } = usePostStoryData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openlogoutModal, setOpenLogoutModal] = useState(false);

  // ── Story state (mirrors mobile storyArrayData / heartModuleData)
  const [storyData, setStoryData] = useState<StoryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [heartModuleData, setHeartModuleData] = useState<{
    blocks: HeartBlock[];
  } | null>(null);

  // ── Room behaviour flags (mirrors mobile setYetiSleeping / setIsSnoozingYeti / setPulsePlannerIcon)
  const [isYetiSleeping, setIsYetiSleeping] = useState(false);
  const [pulsePlannerIcon, setPulsePlannerIcon] = useState(false);
  const [renderHeartModule, setRenderHeartModule] = useState(false);

  const [tapIndicatorTarget, setTapIndicatorTarget] = useState<string | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState<TabType>(TabType.Outdoor);
  const [navigateToJourney, setIsNavigateToJourney] = useState<boolean>(false);
  const [conversationIndex, setConversationIndex] = useState(0);
  const [objectIndex, setObjectIndex] = useState(0);
  const [showOnboardingNavBar, setShowOnboardingNavBar] = useState({
    url: "",
    name: "",
    indicatorType: "",
  });
  /**
   * Mirrors mobile `intialJourneyApiCall`.
   *
   * Step 1 — fetch without ?journeyPage=true
   * Step 2 — if onboarding done OR order > 1, re-fetch with ?journeyPage=true
   * Then branch on the first block of finalTransformedJourneyPage to set state.
   */
  const initialJourneyApiCall = useCallback(async () => {
    setLoading(true);
    setRenderHeartModule(false);
    setIsYetiSleeping(false);
    setPulsePlannerIcon(false);

    // Step 1: plain fetch
    let data = await retrieveStoryData();

    // Step 2: conditional re-fetch with journeyPage flag
    if (data?.isOnboardingCompleted || (data?.order ?? 0) > 1) {
      data = await retrieveStoryData("?journeyPage=true");
    }

    if (!data) {
      setLoading(false);
      return;
    }

    const firstBlock = data.finalTransformedJourneyPage?.blocks?.[0];
    const blockType = firstBlock?.blockType?.toLowerCase() ?? "";
    const template = firstBlock?.template?.toLowerCase() ?? "";
    const blockName = firstBlock?.name ?? "";

    if (blockType === "heart block") {
      // Heart module — show heart corner if onboarding not done
      setHeartModuleData(
        (data.finalTransformedJourneyPage as unknown as {
          blocks: HeartBlock[];
        }) ?? null,
      );
      if (!data.isOnboardingCompleted) {
        setRenderHeartModule(true);
      }
    } else if (template === "snoozing") {
      // Yeti sleeping / snoozing state
      setIsYetiSleeping(true);
      setHeartModuleData(
        (data.preLimitConversation as unknown as { blocks: HeartBlock[] }) ??
          null,
      );
    } else if (
      EXIT_OPTION_NAMES.includes(blockName) &&
      data.isOnboardingCompleted
    ) {
      // Exit option — onboarding done → pulse planner icon
      // setPulsePlannerIcon(true);
      setHeartModuleData(
        (data.preLimitConversation as unknown as { blocks: HeartBlock[] }) ??
          null,
      );
    } else if (
      EXIT_OPTION_NAMES.includes(blockName) &&
      !data.isOnboardingCompleted
    ) {
      // Exit option — onboarding NOT done → show heart module
      setHeartModuleData(
        (data.preLimitConversation as unknown as { blocks: HeartBlock[] }) ??
          null,
      );
      setRenderHeartModule(true);
    } else {
      // Normal story
      setHeartModuleData(
        (data.preLimitConversation as unknown as { blocks: HeartBlock[] }) ??
          null,
      );
    }
    setStoryData(data);

    setLoading(false);
  }, [retrieveStoryData]);

  useEffect(() => {
    initialJourneyApiCall();
  }, [initialJourneyApiCall]);

  // Open HeartCorner when navigated with ?openHeart=true (e.g. from Activities)
  useEffect(() => {
    if (searchParams.get("openHeart") === "true") {
      setRenderHeartModule(true);
      setSearchParams(
        (prev) => {
          prev.delete("openHeart");
          return prev;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  // Mobile updateStoryModule (heartString flow) — POST emotion + refetch
  const handleHeartEmotion = useCallback(
    async (emotion: string) => {
      const blockId = storyData?.blocks?.[0]?.id;
      const storyId = storyData?.storyId;
      if (storyId && blockId) {
        await postStoryData({
          storyId,
          blocks: [{ id: blockId, status: "completed" }],
          emotion,
        });
      }
      setRenderHeartModule(false);
      initialJourneyApiCall();
    },
    [postStoryData, storyData, initialJourneyApiCall],
  );

  // Block browser back / forward buttons while on the Rooms screen.
  // pushState adds a sentinel entry so the first Back press lands here;
  // the popstate handler immediately re-pushes to neutralise every subsequent press.
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Music state — UI only; BackgroundMusicButton owns the actual audio element
  const [musicPlaying, setMusicPlaying] = useState(false);

  // Hamburger dropdown state
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isOnboardingDone = storyData?.isOnboardingCompleted ?? false;
  const onBoardingDoodleImg = storyData?.onBoardingDoodle ?? "";

  // Close hamburger menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  // Conversation engine
  const activeBlock: Block | null = resolveActiveBlock(storyData, activeTab);
  const currentConversations = activeBlock?.conversations ?? [];
  const currentObjects = currentConversations[conversationIndex]?.object ?? [];
  const currentObject = currentObjects[objectIndex] ?? null;
  const conversationsExhausted =
    conversationIndex >= currentConversations.length &&
    currentConversations.length > 0;

  const advanceObject = useCallback(() => {
    if (objectIndex < currentObjects.length - 1) {
      setObjectIndex((i) => i + 1);
    } else if (conversationIndex < currentConversations.length - 1) {
      setConversationIndex((i) => i + 1);
      setObjectIndex(0);
    }
  }, [
    objectIndex,
    conversationIndex,
    currentObjects.length,
    currentConversations.length,
  ]);

  useEffect(() => {
    if (!currentObject || conversationsExhausted) return;
    const delay =
      (parseInt(currentObject.duration ?? "3000", 10) || 3000) + 1000;
    const timer = setTimeout(advanceObject, delay);
    return () => clearTimeout(timer);
  }, [currentObject, conversationsExhausted, advanceObject]);

  const handleTabPress = (tab: TabType) => {
    if (tab === activeTab) return;
    trackEvent(TrackingEvents.room_entered, {
      room: TabMappedToRoom[tab],
    });
    setActiveTab(tab);
    setConversationIndex(0);
    setObjectIndex(0);
  };

  const handleUserAction = (action: UserAction) => {
    console.log("user_action:", action);
  };

  // Logout — mirrors mobile's hamburger → drawer → logout
  const handleLogout = () => triggerLogout("manual");

  const showHeart =
    activeTab === TabType.Character || activeTab === TabType.Indoor;

  const handleJourneySplashDone = useCallback(
    () => setIsNavigateToJourney(false),
    [],
  );

  const updateStoryModule = useCallback(
    async (
      text?: string,
      templateObject?: { name: string; status: boolean }[],
      heartString?: string,
    ) => {
      setShowOnboardingNavBar({ url: "", name: "", indicatorType: "" });
      setTapIndicatorTarget(null);
      setLoading(true);
      setConversationIndex(0);
      setObjectIndex(0);
      const blockId = storyData?.blocks?.[0]?.id ?? "";
      let bodyData: StoryUpdatePayload = {
        storyId: storyData?.storyId,
        blocks: [{ id: blockId, status: "completed" }],
      };

      if (templateObject) {
        trackEvent(TrackingEvents.interest_selected, {
          interests: templateObject.map((item) => item.name),
        });
        bodyData = { ...bodyData, template: templateObject };
      }
      if (text) {
        trackEvent(TrackingEvents.name_entered, { name: text.trim() });
        bodyData = { ...bodyData, name: text.trim() };
      }
      if (heartString) {
        bodyData = { ...bodyData, emotion: heartString };
      }

      trackEvent(TrackingEvents.child_onboarding_block_completed, {
        block_name: storyData?.blocks?.[0]?.name,
        block_type: storyData?.blocks?.[0]?.blockType,
        template_name: storyData?.blocks?.[0]?.template,
      });

      const postDataVal = await postStoryData(bodyData);
      if (postDataVal?.blocks?.length > 0) {
        trackEvent(TrackingEvents.child_onboarding_block_started, {
          block_name: postDataVal.blocks[0]?.name,
          block_type: postDataVal.blocks[0]?.blockType,
          template_name: postDataVal.blocks[0]?.template,
        });
      }
      await initialJourneyApiCall();
    },
    [storyData, postStoryData, initialJourneyApiCall],
  );

  const roomProps = {
    storyData,
    activeBlock,
    conversationIndex,
    objectIndex,
    onUserAction: handleUserAction,
    onNavigateToJourney: () => setIsNavigateToJourney(true),
    onTabChange: handleTabPress,
    onInputSubmit: (value: string) => updateStoryModule(value),
    onTemplateSubmit: (template: { name: string; status: boolean }[]) =>
      updateStoryModule(undefined, template),
    onButtonAction: () => updateStoryModule(),
    onTapIndicator: (name: string | null) => {
      setTapIndicatorTarget(name);
      if (name === "MusicIcon") setMusicPlaying(true);
    },
    onShowOnboardingNavBar: (values: {
      url: string;
      name: string;
      indicatorType: string;
    }) => setShowOnboardingNavBar(values),
  };

  const renderRoom = () => {
    const templateType = storyData?.blocks?.[0]?.template;
    if (isOnboardingDone) {
      switch (activeTab) {
        case TabType.Outdoor:
          return <YardRoom {...roomProps} />;
        case TabType.Indoor:
          return <HomeRoom {...roomProps} />;
        case TabType.Explore:
          return <PlayRoom {...roomProps} />;
        case TabType.Character:
          return <CalmRoom {...roomProps} />;
      }
    } else {
      if (
        templateType === childOnboardingTemplateType.yard ||
        templateType === childOnboardingTemplateType.onBoardingYard
      ) {
        return <YardRoom {...roomProps} />;
      }
      if (templateType === childOnboardingTemplateType.home) {
        return <HomeRoom {...roomProps} />;
      }
      if (templateType === childOnboardingTemplateType.calmCorner) {
        return <CalmRoom {...roomProps} />;
      }
      if (
        templateType === childOnboardingTemplateType.funRoom ||
        templateType === childOnboardingTemplateType.OnboardingCalmRoom ||
        templateType === childOnboardingTemplateType.OnBoardingPlayRoom
      ) {
        return <PlayRoom {...roomProps} />;
      }
      if (templateType === childOnboardingTemplateType.OnBoardingWelcomeRoom) {
        return (
          <OnBoardingWelcomeRoom
            isOnboardingDone={storyData?.isOnboardingCompleted}
            storyArrayData={storyData}
            updateState={() => updateStoryModule()}
            onBoardingDoodleImg={onBoardingDoodleImg}
          />
        );
      }
      if (templateType === childOnboardingTemplateType.OnBoardingBalloon) {
        return (
          <OnBoardingWelcomeTemplate
            isOnboardingDone={storyData?.isOnboardingCompleted}
            storyArrayData={storyData}
            updateState={() => updateStoryModule()}
          />
        );
      }

      // Onboarding not done — show HomeRoom for all tabs, with conditional content based on activeBlock
      return <HomeRoom {...roomProps} />;
    }
  };

  // ── Mobile pattern: early-return HeartCorner inline when active ──
  if (renderHeartModule && heartModuleData?.blocks?.length) {
    return (
      <HeartCorner
        conversationValue={heartModuleData.blocks[0]}
        allBlocksData={heartModuleData}
        updateApiCall={handleHeartEmotion}
        onClose={() => setRenderHeartModule(false)}
      />
    );
  }

  return (
    <div className="rooms">
      {loading && <WebLoader />}
      {navigateToJourney && (
        <WebJourneySplash updateState={handleJourneySplashDone} />
      )}

      <div className="rooms__scene">{renderRoom()}</div>

      {/* ── Top-right controls — mirrors mobile musicIconContainer ── */}
      {!loading && (
        <div className="rooms__top-controls">
          {/* Music toggle — BackgroundMusic equivalent */}

          <div
            className={`rooms__ctrl-btn${musicPlaying ? " rooms__ctrl-btn--active" : ""}${tapIndicatorTarget === "MusicIcon" ? " rooms__ctrl-btn--glow" : ""}`}
          >
            <BackgroundMusicButton
              playing={tapIndicatorTarget === "MusicIcon" ? true : undefined}
              onPlayStateChange={(isPlaying: boolean) =>
                setMusicPlaying(isPlaying)
              }
            />
          </div>
          {isOnboardingDone && (
            <div className={`rooms__ctrl-btn`}>
              <button
                className="rooms__ctrl-btn"
                aria-label="Go to Dashboard"
                onClick={() => {
                  localStorage.setItem("selectedMode", "admin");
                  window.location.href = "/dashboard";
                }}
              >
                <Lottie
                  animationData={appSwitchIcon}
                  loop={false}
                  autoplay={false}
                  className="rooms__ctrl-lottie"
                />
              </button>
            </div>
          )}

          {/* <button
            className={`rooms__ctrl-btn${musicPlaying ? " rooms__ctrl-btn--active" : ""}`}
            aria-label={musicPlaying ? "Pause music" : "Play music"}
            onClick={() => setMusicPlaying((p) => !p)}
          > */}
          {/* <Lottie
              animationData={!musicPlaying ? activeMusicJson : musicBoxJson}
              loop={musicPlaying}
              autoplay={musicPlaying}
              className="rooms__ctrl-lottie"
            /> */}
          {/* </button> */}

          {/* Hamburger — always visible, opens logout menu (drawer equivalent) */}
          <div className="rooms__menu-wrap" ref={menuRef}>
            <button
              className="rooms__ctrl-btn"
              aria-label="Menu"
              // onClick={() => setMenuOpen((o) => !o)}
              onClick={() => setOpenLogoutModal(true)}
            >
              <Lottie
                animationData={logoutIcon}
                loop={false}
                autoplay={false}
                className="rooms__ctrl-lottie"
              />
            </button>

            {/* {menuOpen && (
              <div className="rooms__dropdown">
                <button className="rooms__dropdown-item" onClick={handleLogout}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Logout
                </button>
              </div>
            )} */}
          </div>
        </div>
      )}

      {/* ── Heart — bottom-left (CalmRoom + HomeRoom).
          Hidden when yeti is sleeping (mobile: sleeping state = no heart interaction). */}
      {showHeart && (
        // {showHeart && !isYetiSleeping && (
        <button
          className="rooms__heart-btn"
          aria-label="Heart"
          onClick={() => setRenderHeartModule(true)}
        >
          <Lottie
            animationData={heartImageJson}
            loop
            autoplay
            className="rooms__heart-lottie"
          />
        </button>
      )}

      {/* ── Tab bar ── */}
      {isOnboardingDone && (
        <RoomTabBar activeTab={activeTab} onTabPress={handleTabPress} />
      )}

      {/* ── Onboarding Nav bar — centered bottom, shown when blockObject name is "Nav" ── */}
      {showOnboardingNavBar.name === "Nav" && showOnboardingNavBar.url && (
        <button
          type="button"
          className="rooms__onboarding-navbar"
          onClick={() => updateStoryModule()}
        >
          <WebLottiePlayer
            source={showOnboardingNavBar.url}
            className="rooms__onboarding-navbar-lottie"
            loop
            autoPlay
          />
          {/* {showOnboardingNavBar?.indicatorType && (
            <div
              className="yard-room__homebtn-indicator"
              // style={{ justifyContent: "flex-end" }}
            >
              <Lottie
                animationData={clickAnimationJson}
                loop
                autoplay
                className="yard-room__tap-indicator-lottie"
              />
            </div>
          )} */}
        </button>
      )}
      {openlogoutModal && (
        <ConfirmModal
          isOpen={openlogoutModal}
          title="Confirm Logout"
          message="Are you sure you want to log out?"
          confirmText="Log out"
          cancelText="Cancel"
          onConfirm={handleLogout}
          onCancel={() => setOpenLogoutModal(false)}
        />
      )}
    </div>
  );
}

export default Rooms;
